/**
 * @file use-coupon-form-state.ts
 *
 * Controlled-form state for the coupon form (no react-hook-form). Holds the
 * editable fields, exposes a `patch` updater, tracks whether the form is dirty
 * against its seed, and runs hand-rolled validation (required code; a valid,
 * non-negative amount with a 100% ceiling for percentage coupons; an expiry not
 * in the past; non-negative limits). Validation errors surface only after the
 * first `validate()` call (submit) so the form doesn't shout at an untouched
 * field — mirroring `use-product-form-state` / `use-category-form-state`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import type { ShopCoupon } from '../../api/shop-types';
import { adminMessages } from '../../i18n/messages';
import {
  deriveFields,
  isInvalidAmount,
  isInvalidOptionalAmount,
  isInvalidUsageLimit,
  isPastDate,
  isPercentTooHigh,
  shallowEqual,
} from './coupon-form-utils';
import type { CouponFormErrors, CouponFormFields } from './coupon-form-types';

/** What {@link useCouponFormState} returns to the form. */
export interface UseCouponFormStateResult {
  fields: CouponFormFields;
  patch: (update: Partial<CouponFormFields>) => void;
  isDirty: boolean;
  errors: CouponFormErrors;
  validate: () => boolean;
  /** Attach to the code field; focused/scrolled to when validation fails. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the coupon form's field state and validation.
 *
 * @param coupon - The seed coupon (edit), or `undefined` to create.
 *
 * @returns The fields, updater, dirty flag, errors, and a `validate` function.
 */
export function useCouponFormState(
  coupon: ShopCoupon | undefined,
): UseCouponFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<CouponFormFields>(() => deriveFields(coupon));
  const initialRef = useRef<CouponFormFields>(fields);
  const seededCouponRef = useRef(coupon);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Re-seed only when the source coupon reference changes (e.g. it resolves
  // after a fetch); the initial value is already seeded by `useState`.
  useEffect(() => {
    if (seededCouponRef.current === coupon) {
      return;
    }
    seededCouponRef.current = coupon;
    const seeded = deriveFields(coupon);
    setFields(seeded);
    initialRef.current = seeded;
    setSubmitted(false);
  }, [coupon]);

  const patch = useCallback((update: Partial<CouponFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: CouponFormFields): CouponFormErrors => {
      const errors: CouponFormErrors = {};
      if (current.code.trim() === '') {
        errors.codeError = intl.formatMessage(adminMessages.couponsCodeRequired);
      }
      if (current.amount.trim() === '') {
        errors.amountError = intl.formatMessage(adminMessages.couponsAmountRequired);
      } else if (isInvalidAmount(current.amount)) {
        errors.amountError = intl.formatMessage(adminMessages.couponsAmountInvalid);
      } else if (isPercentTooHigh(current.discount_type, current.amount)) {
        errors.amountError = intl.formatMessage(adminMessages.couponsAmountPercentTooHigh);
      }
      if (isPastDate(current.date_expires)) {
        errors.dateExpiresError = intl.formatMessage(adminMessages.couponsExpiryInPast);
      }
      if (isInvalidUsageLimit(current.usage_limit)) {
        errors.usageLimitError = intl.formatMessage(adminMessages.couponsUsageLimitInvalid);
      }
      if (isInvalidOptionalAmount(current.minimum_amount)) {
        errors.minimumAmountError = intl.formatMessage(
          adminMessages.couponsMinimumAmountInvalid,
        );
      }
      if (isInvalidOptionalAmount(current.maximum_amount)) {
        errors.maximumAmountError = intl.formatMessage(
          adminMessages.couponsMaximumAmountInvalid,
        );
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.codeError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(() => !shallowEqual(fields, initialRef.current), [fields]);

  return { fields, patch, isDirty, errors, validate, firstErrorRef };
}
